/**
 * Specialty metadata — now backed by the Specialty Registry.
 *
 * rev v11.0.0 · Phase 2 / C1
 *
 * WHAT CHANGED
 * ------------
 * This file used to hold its own list of 15 specialties, duplicating the
 * one in the catalogue. It is now a thin adapter over
 * `@dikdoc/specialty-registry`, which is the single authoring surface for
 * the specialty vocabulary.
 *
 * The catalogue counts are no longer computed from the 981 KB JSON: they
 * come from the registry manifest, which the CI drift gate keeps in step
 * with the real catalogue. The acts themselves are loaded lazily, one
 * chunk per specialty.
 */
import { SPECIALTIES as REGISTRY_SPECIALTIES } from "@dikdoc/specialty-registry";
import {
  loadSpecialtyActs,
  subSpecialtiesOf,
  type NgapAct,
} from "@/lib/ngap-catalogue";

export type NgapSpecialtyMeta = {
  id: string;
  label: string;
  short: string;
  scope: string;
  doctorSpecs: string[];
};

/**
 * The specialty metadata, in clinical order.
 *
 * Backed by the registry, so a manifest change reaches this list without
 * a second edit.
 */
export const SPECIALTY_META: NgapSpecialtyMeta[] = REGISTRY_SPECIALTIES.map((s) => ({
  id: s.id,
  label: s.label,
  short: s.short,
  scope: s.scope,
  doctorSpecs: s.doctorSpecs,
}));

export type SpecialtyWithCounts = NgapSpecialtyMeta & {
  count: number;
  subSpecialties: { id: string; count: number }[];
};

/**
 * The specialties with their catalogue act counts.
 *
 * `subSpecialties` is empty here on purpose: it can only be computed from
 * the loaded chunk, and loading fifteen chunks to render a tab bar would
 * defeat the lazy loading. Use `loadSpecialtyCatalogue()` for the acts and
 * their sub-specialties.
 */
export const SPECIALTIES: SpecialtyWithCounts[] = SPECIALTY_META.map((s) => {
  const registry = REGISTRY_SPECIALTIES.find((r) => r.id === s.id);
  return {
    ...s,
    count: registry?.ngap.catalogueActs ?? 0,
    subSpecialties: [],
  };
});

/** The acts of a specialty, loaded on demand. */
export async function loadSpecialtyCatalogue(
  specialtyId: string,
): Promise<{ acts: NgapAct[]; subSpecialties: { id: string; count: number }[] }> {
  const acts = await loadSpecialtyActs(specialtyId);
  return { acts, subSpecialties: subSpecialtiesOf(acts) };
}

/**
 * The acts of a specialty, optionally filtered by sub-specialty.
 *
 * The asynchronous twin of the former synchronous `codesFor`. It always
 * loads the whole chunk first — the chunk IS the sub-specialty partition.
 */
export async function codesFor(
  specialtyId: string,
  subSpecialty?: string | null,
): Promise<NgapAct[]> {
  const { acts } = await loadSpecialtyCatalogue(specialtyId);
  if (!subSpecialty || subSpecialty === "all") return acts;
  return acts.filter((a) => (a.subSpecialty || "Général") === subSpecialty);
}

/** Find one act by its NGAP code, across every specialty. */
export async function findNGAPByCode(code: string): Promise<NgapAct | undefined> {
  const { loadAllActs } = await import("@/lib/ngap-catalogue");
  const all = await loadAllActs();
  return all.find((a) => a.code === code);
}
